import React from 'react';
import type { TagProps, TagTone } from './types';

const TONE_CLASS: Record<TagTone, string> = {
    success: 'bg-surface-badge-success text-success-100',
    danger: 'bg-surface-badge-danger text-danger',
    warning: 'bg-surface-badge-warning text-warning',
    info: 'bg-surface-badge-info text-info',
    note: 'bg-surface-note text-note',
    neutral: 'bg-surface-neutral text-neutral',
    level1: 'bg-surface-alert-100 text-alert-100',
    level2: 'bg-surface-alert-200 text-alert-200',
};

// Shared by the text and outline appearances. Neutral takes the subtle text colour so it reads like
// the plain metadata around it.
const TONE_TEXT_CLASS: Record<TagTone, string> = {
    success: 'text-success-100',
    danger: 'text-danger',
    warning: 'text-warning',
    info: 'text-info',
    note: 'text-note',
    neutral: 'text-content-subtle',
    level1: 'text-alert-100',
    level2: 'text-alert-200',
};

/**
 * Compact status/trend label for lists and tables — "+5pts", "Warning", "Stable". Not `Banner`:
 * that one is a contextual notice with its own surface region, this one sits inline beside other content.
 */
export const Tag: React.FC<TagProps> = ({
    tone, icon, iconPosition = 'start', appearance = 'pill', children, testId, className = '', 'aria-describedby': describedBy,
}) => {
    const isText = appearance === 'text';
    const look = {
        pill: `rounded-full px-s ${TONE_CLASS[tone]}`,
        outline: `rounded-full border border-line px-s ${TONE_TEXT_CLASS[tone]}`,
        text: TONE_TEXT_CLASS[tone],
    }[appearance];
    const glyph = icon && (
        <span className={`flex ${isText ? 'h-3.5 w-3.5' : 'h-3 w-3'} shrink-0 items-center justify-center [&>svg]:h-full [&>svg]:w-full`} aria-hidden="true">
            {icon}
        </span>
    );

    return (
        <span
            data-testid={testId}
            aria-describedby={describedBy}
            className={
                `inline-flex h-5 items-center gap-xs text-xs font-semibold whitespace-nowrap ` +
                `${look} ${className}`
            }
        >
            {iconPosition === 'start' && glyph}
            {children}
            {iconPosition === 'end' && glyph}
        </span>
    );
};
